/**
 * LLM Router — multi-armed bandit model selection with per-callsite scoring.
 *
 * Arms are keyed per-model (provider:modelId), not per-provider.
 * Uses Thompson Sampling with exploration multiplier for cold-start.
 *
 * Self-learning: every LLM call feeds back into the scoring system,
 * so the router converges on the best model for each task type.
 */

import type { Provider } from "./types";
import { armKey, getModelEntry } from "./models";
import type { ModelCapability } from "./models";
import { pushGrimoireObservation } from "./grimoire";

/** Scoring dimensions for LLM output quality. */
export interface ScoreDimensions {
  accuracy: number; // 0-1: factual/mechanical correctness
  completeness: number; // 0-1: did it include all requested elements
  creativity: number; // 0-1: narrative quality, surprise, engagement
  speed: number; // 0-1: normalized response time (1 = fast)
  cost: number; // 0-1: normalized cost efficiency (1 = cheap)
}

/** Weights for each dimension, varies by call site. */
export type DimensionWeights = ScoreDimensions;

/** Call site identifiers — where in the game pipeline the LLM is called. */
export type CallSite =
  | "narrative-turn"
  | "opening-scene"
  | "option-generation"
  | "background-generation"
  | "character-assignment"
  | "world-description"
  | "animation-frame"
  | "beat-extraction"
  | "arc-generation"
  | "memory-compression"
  | "beat-summary";

/** Per-callsite dimension weights — what matters most at each layer. */
export const CALLSITE_WEIGHTS: Record<CallSite, DimensionWeights> = {
  "narrative-turn": { accuracy: 0.15, completeness: 0.2, creativity: 0.4, speed: 0.15, cost: 0.1 },
  "opening-scene": { accuracy: 0.1, completeness: 0.25, creativity: 0.45, speed: 0.1, cost: 0.1 },
  "option-generation": {
    accuracy: 0.3,
    completeness: 0.35,
    creativity: 0.2,
    speed: 0.1,
    cost: 0.05,
  },
  "background-generation": {
    accuracy: 0.2,
    completeness: 0.3,
    creativity: 0.35,
    speed: 0.1,
    cost: 0.05,
  },
  "character-assignment": {
    accuracy: 0.45,
    completeness: 0.3,
    creativity: 0.1,
    speed: 0.1,
    cost: 0.05,
  },
  "world-description": {
    accuracy: 0.15,
    completeness: 0.25,
    creativity: 0.4,
    speed: 0.1,
    cost: 0.1,
  },
  "animation-frame": { accuracy: 0.1, completeness: 0.15, creativity: 0.3, speed: 0.35, cost: 0.1 },
  "beat-extraction": { accuracy: 0.4, completeness: 0.3, creativity: 0.05, speed: 0.15, cost: 0.1 },
  "arc-generation": { accuracy: 0.2, completeness: 0.3, creativity: 0.35, speed: 0.1, cost: 0.05 },
  "memory-compression": {
    accuracy: 0.35,
    completeness: 0.35,
    creativity: 0.05,
    speed: 0.15,
    cost: 0.1,
  },
  "beat-summary": { accuracy: 0.3, completeness: 0.4, creativity: 0.05, speed: 0.15, cost: 0.1 },
};

/** Cost tier for each callsite — controls which models are eligible. */
export type CostTier = "flash" | "pro" | "any";

/**
 * Per-callsite cost tier — filters models before Thompson Sampling.
 *
 * - "flash": only fast/cheap models (speedClass "fast" or avg cost < $0.001/1k tokens)
 * - "pro": all models eligible (expensive tasks can use any model)
 * - "any": no filtering
 */
export const CALLSITE_TIER: Record<CallSite, CostTier> = {
  "narrative-turn": "pro",
  "opening-scene": "pro",
  "option-generation": "flash",
  "background-generation": "flash",
  "character-assignment": "flash",
  "world-description": "pro",
  "animation-frame": "flash",
  "beat-extraction": "flash",
  "arc-generation": "flash",
  "memory-compression": "flash",
  "beat-summary": "flash",
};

/** Cost threshold for flash-tier eligibility: average of input+output per 1k tokens. */
const FLASH_COST_THRESHOLD = 0.001;

/**
 * Per-callsite required capabilities — models must have ALL listed capabilities.
 *
 * Only callsites that need specific capabilities are listed.
 * Omitted callsites have no capability requirements.
 */
export const CALLSITE_REQUIRED_CAPABILITIES: Partial<Record<CallSite, ModelCapability[]>> = {
  "option-generation": ["structured"],
  "character-assignment": ["structured"],
  "beat-extraction": ["structured"],
  "beat-summary": ["structured"],
};

/**
 * Check whether a model qualifies as "flash" tier.
 *
 * A model is flash-eligible if it has speedClass "fast" OR
 * its average per-1k-token cost is below the threshold.
 */
export function isFlashEligible(provider: Provider, modelId: string): boolean {
  const entry = getModelEntry(provider, modelId);
  if (!entry) return false;
  if (entry.speedClass === "fast") return true;
  const avgCost = (entry.costPer1kInput + entry.costPer1kOutput) / 2;
  return avgCost < FLASH_COST_THRESHOLD;
}

/** A scored observation from an LLM call. */
export interface LLMCallMetric {
  provider: Provider;
  modelId: string;
  callSite: CallSite;
  scores: ScoreDimensions;
  latencyMs: number;
  tokenCount: number;
  timestamp: number;
}

/** Beta distribution parameters for Thompson Sampling. */
interface BetaParams {
  alpha: number; // successes + 1
  beta: number; // failures + 1
}

/** The router's state — serializable for persistence. */
interface RouterState {
  /** Beta params per arm key (provider:modelId) per call site. */
  arms: Record<string, Record<string, BetaParams>>;
  /** Rolling window of recent observations (last 100). */
  history: LLMCallMetric[];
  /** Total calls per arm key. */
  callCounts: Record<string, number>;
  /** Per-callsite exploration multipliers. */
  explorationMultipliers: Partial<Record<CallSite, number>>;
}

/**
 * Sample from a Gamma(shape, 1) distribution.
 *
 * Uses Marsaglia and Tsang's method for shape >= 1, and
 * Ahrens-Dieter's boost for shape < 1: Gamma(a) = Gamma(a+1) * U^(1/a).
 */
function sampleGamma(shape: number): number {
  if (shape < 1) {
    // Boost: Gamma(a, 1) = Gamma(a+1, 1) * U^(1/a)
    return sampleGamma(shape + 1) * Math.pow(Math.random(), 1 / shape);
  }

  // Marsaglia and Tsang's method for shape >= 1
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);

  for (;;) {
    let x: number;
    let v: number;

    // Generate a standard normal via Box-Muller
    do {
      x = Math.sqrt(-2 * Math.log(Math.random())) * Math.cos(2 * Math.PI * Math.random());
      v = 1 + c * x;
    } while (v <= 0);

    v = v * v * v;
    const u = Math.random();

    // Squeeze test then full acceptance check
    if (u < 1 - 0.0331 * (x * x) * (x * x)) return d * v;
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v;
  }
}

/**
 * Sample from a Beta(alpha, beta) distribution.
 *
 * Uses the standard Gamma-ratio method: if X ~ Gamma(a) and Y ~ Gamma(b),
 * then X / (X + Y) ~ Beta(a, b).
 */
export function sampleBeta(alpha: number, beta: number): number {
  const x = sampleGamma(alpha);
  const y = sampleGamma(beta);
  if (x + y === 0) return 0.5; // degenerate case safety
  return x / (x + y);
}

/** Normalize a latency to 0-1 where lower latency = higher score. */
export function normalizeSpeed(latencyMs: number): number {
  // 1s = 1.0, 60s = 0.0, clamped to [0, 1]
  return Math.max(0, Math.min(1, 1 - (latencyMs - 1000) / 59000));
}

/**
 * Normalize cost to 0-1 where lower cost = higher score.
 *
 * Accepts raw per-1k-token costs directly (no internal cost table).
 * Uses the average of input and output costs for simplicity.
 */
export function normalizeCost(
  tokenCount: number,
  costPer1kInput: number,
  costPer1kOutput: number,
): number {
  const avgCostPer1k = (costPer1kInput + costPer1kOutput) / 2;
  const cost = (tokenCount / 1000) * avgCostPer1k;
  // $0 = 1.0, $0.05 = 0.0, clamped to [0, 1]
  return Math.max(0, Math.min(1, 1 - cost / 0.05));
}

/**
 * Filter available models by cost tier and required capabilities for a callsite.
 *
 * Applies two filtering passes in order:
 * 1. Cost tier: "flash" callsites only allow fast/cheap models; "pro" and "any" allow all.
 * 2. Capabilities: if the callsite requires specific capabilities, only models with ALL
 *    required capabilities pass.
 *
 * If either pass eliminates all candidates, falls back to the unfiltered set to ensure
 * the router always has at least one model to sample from.
 */
export function filterModelsForCallsite<T extends { provider: Provider; modelId: string }>(
  callSite: CallSite,
  models: T[],
): T[] {
  if (models.length === 0) return models;

  // --- Pass 1: Cost tier filtering ---
  const tier = CALLSITE_TIER[callSite] ?? "any";
  let filtered: T[];

  if (tier === "flash") {
    filtered = models.filter((m) => isFlashEligible(m.provider, m.modelId));
    // Fall back to all models if no flash models have keys
    if (filtered.length === 0) filtered = models;
  } else {
    // "pro" and "any" allow all models
    filtered = models;
  }

  // --- Pass 2: Capability filtering ---
  const requiredCaps = CALLSITE_REQUIRED_CAPABILITIES[callSite];
  if (requiredCaps && requiredCaps.length > 0) {
    const capFiltered = filtered.filter((m) => {
      const entry = getModelEntry(m.provider, m.modelId);
      if (!entry) return false;
      return requiredCaps.every((cap) => entry.capabilities.includes(cap));
    });
    // Fall back to tier-filtered set if capability filtering is too aggressive
    if (capFiltered.length > 0) filtered = capFiltered;
  }

  return filtered;
}

/**
 * Multi-armed bandit LLM router using Thompson Sampling.
 *
 * Arms are keyed per-model (provider:modelId) with independent
 * Beta distributions per call site. Supports exploration multipliers
 * for cold-start dampening and serialization for persistence.
 */
export class LLMRouter {
  private state: RouterState;

  constructor() {
    this.state = {
      arms: {},
      history: [],
      callCounts: {},
      explorationMultipliers: {},
    };
  }

  /** Get or create Beta params for a model at a call site. */
  private getArm(provider: Provider, modelId: string, callSite: CallSite): BetaParams {
    const key = armKey(provider, modelId);
    if (!this.state.arms[key]) {
      this.state.arms[key] = {};
    }
    if (!this.state.arms[key][callSite]) {
      // Uniform prior — no bias
      this.state.arms[key][callSite] = { alpha: 1, beta: 1 };
    }
    return this.state.arms[key][callSite];
  }

  /**
   * Select the best model for a call site using Thompson Sampling.
   *
   * Before sampling, filters models by cost tier and required capabilities.
   * Falls back to the unfiltered set if filtering eliminates all candidates.
   *
   * Samples from each model's Beta distribution (dampened by the exploration
   * multiplier) and returns the model with the highest sample.
   */
  sampleArm(
    callSite: CallSite,
    availableModels: { provider: Provider; modelId: string; key: string }[],
  ): { provider: Provider; modelId: string; key: string } {
    const candidates = filterModelsForCallsite(callSite, availableModels);
    const multiplier = this.state.explorationMultipliers[callSite] ?? 1;
    let best = candidates[0];
    let bestScore = -1;

    for (const model of candidates) {
      const arm = this.getArm(model.provider, model.modelId, callSite);
      // Dampen toward uniform prior: effectiveAlpha approaches 1 as multiplier grows
      const effectiveAlpha = 1 + (arm.alpha - 1) / multiplier;
      const effectiveBeta = 1 + (arm.beta - 1) / multiplier;
      const score = sampleBeta(effectiveAlpha, effectiveBeta);
      if (score > bestScore) {
        bestScore = score;
        best = model;
      }
    }

    return best;
  }

  /**
   * Set the exploration multiplier for a specific call site.
   *
   * Higher values (e.g., 10) dampen exploitation, forcing more uniform
   * exploration. Decays toward 1 as the system gathers observations.
   */
  setExplorationMultiplier(callSite: CallSite, value: number): void {
    this.state.explorationMultipliers[callSite] = value;
  }

  /**
   * Record an observation from an LLM call.
   *
   * Updates the Beta distribution for the model at this call site.
   * The weighted score (based on callsite dimension weights) is split
   * into alpha (success) and beta (failure) increments.
   */
  recordObservation(metric: LLMCallMetric): void {
    const weights = CALLSITE_WEIGHTS[metric.callSite];

    // Compute weighted score across all dimensions
    const weightedScore =
      metric.scores.accuracy * weights.accuracy +
      metric.scores.completeness * weights.completeness +
      metric.scores.creativity * weights.creativity +
      metric.scores.speed * weights.speed +
      metric.scores.cost * weights.cost;

    // Update Beta params: proportional success/failure
    const arm = this.getArm(metric.provider, metric.modelId, metric.callSite);
    arm.alpha += weightedScore;
    arm.beta += 1 - weightedScore;

    // Decay old observations to stay adaptive (exponential recency).
    // Every 50 observations per arm key, decay ALL arms slightly toward prior.
    const key = armKey(metric.provider, metric.modelId);
    const totalCalls = (this.state.callCounts[key] || 0) + 1;
    this.state.callCounts[key] = totalCalls;
    if (totalCalls % 50 === 0) {
      for (const ak of Object.keys(this.state.arms)) {
        for (const site of Object.keys(this.state.arms[ak])) {
          const a = this.state.arms[ak][site];
          a.alpha = Math.max(1, a.alpha * 0.9);
          a.beta = Math.max(1, a.beta * 0.9);
        }
      }
    }

    // Add to rolling history (keep last 100)
    this.state.history.push(metric);
    if (this.state.history.length > 100) {
      this.state.history = this.state.history.slice(-100);
    }

    // Push to Grimoire cross-project exchange (fire-and-forget)
    pushGrimoireObservation(metric);
  }

  /** Serialize router state to a JSON string for persistence. */
  serialize(): string {
    return JSON.stringify(this.state);
  }

  /** Restore router state from a previously serialized JSON string. */
  deserialize(data: string): void {
    this.state = JSON.parse(data) as RouterState;
  }

  /** Get performance summary for display in settings. */
  getStats(): Record<string, { calls: number; avgScore: number }> {
    const stats: Record<string, { calls: number; avgScore: number }> = {};

    for (const obs of this.state.history) {
      const key = armKey(obs.provider, obs.modelId);
      if (!stats[key]) {
        stats[key] = { calls: 0, avgScore: 0 };
      }
      const s = stats[key];
      const weights = CALLSITE_WEIGHTS[obs.callSite];
      const score =
        obs.scores.accuracy * weights.accuracy +
        obs.scores.completeness * weights.completeness +
        obs.scores.creativity * weights.creativity +
        obs.scores.speed * weights.speed +
        obs.scores.cost * weights.cost;
      s.avgScore = (s.avgScore * s.calls + score) / (s.calls + 1);
      s.calls++;
    }

    return stats;
  }

  /** Reset all learning data (fresh start). */
  reset(): void {
    this.state = {
      arms: {},
      history: [],
      callCounts: {},
      explorationMultipliers: {},
    };
  }
}

/** Default singleton instance for backward compatibility. */
export const llmRouter = new LLMRouter();
